import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Truck, ArrowLeft, Edit, AlertTriangle, X } from 'lucide-react';
import MainService from '../../services/MainService';

const VehicleDetails = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (vehicleId) {
      loadVehicleDetails();
    }
  }, [vehicleId]);

  const loadVehicleDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.VehicleById(vehicleId);
      const id = response?.Id ?? response?.ID;
      if (response && id != null) {
        setVehicle(response);
      } else {
        setVehicle(null);
        setError('Vehicle not found');
      }
    } catch (err) {
      console.error('Error loading vehicle details:', err);
      setVehicle(null);
      setError('Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/home/vehicle-management/add-edit-vehicle/${vehicleId}`);
  };

  const plate = vehicle ? vehicle.Number || vehicle.PlateNumber : '';

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/home/vehicle-management/vehicle-list')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center">
              <Truck className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Vehicle Details</h1>
            </div>
          </div>
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Details Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : vehicle ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Vehicle Number</label>
              <p className="text-sm text-gray-900">{plate || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Model</label>
              <p className="text-sm text-gray-900">{vehicle.Model || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Make</label>
              <p className="text-sm text-gray-900">{vehicle.Make || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Year</label>
              <p className="text-sm text-gray-900">
                {vehicle.Year != null && vehicle.Year !== '' ? String(vehicle.Year) : '-'}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Color</label>
              <p className="text-sm text-gray-900">{vehicle.Color || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Registration Number</label>
              <p className="text-sm text-gray-900">{vehicle.RegistrationNumber || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Chassis Number</label>
              <p className="text-sm text-gray-900">{vehicle.ChassisNumber || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Engine Number</label>
              <p className="text-sm text-gray-900">{vehicle.EngineNumber || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <p className="text-sm">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  vehicle.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {vehicle.IsActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-gray-500">Unable to load vehicle details.</div>
        ) : (
          <div className="text-center py-8 text-gray-500">No vehicle data.</div>
        )}
      </div>
    </div>
  );
};

export default VehicleDetails;

